import React, { type FC, type ReactNode, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from 'antd';
import { observer } from 'mobx-react-lite';
import { Space, Button } from '@g2u/ui-kit';
import ReactDOM from 'react-dom';

import { Content, Intro, JobConfirmation, LegacyCustomerAlert, SendNotification, CoreBmsJobLink } from 'components';
import { useSendJobNotification } from 'helpers//hooks/useSendJobNotifications';
import { useJob } from 'helpers';
import { FinalAssistance } from './components';
import { BookingStore } from 'store';
import { JsonDataItem } from 'types';
import styles from './confirmation.module.scss';

export const BookingConfirmationPage: FC = observer(() => {
  const { setStep, technician, isNotified, setIsNotified, resetBookingProgress } = BookingStore;
  const navigate = useNavigate();
  const { id } = useParams();
  const { job } = useJob(id);

  const {
    sendNotifications,
    isNotifying,
    initialValues,
    emailOptions,
    smsOptions,
    isNotified: isNotificationSent,
  } = useSendJobNotification(job, technician, SendNotification.Type.NewService);

  useEffect(() => {
    if (isNotificationSent) {
      setIsNotified(isNotificationSent);
    }
  }, [isNotificationSent]);

  const handleSendNotifications = (data: JsonDataItem | null) => {
    data ? sendNotifications(data) : setIsNotified(true);
  };

  useEffect(() => {
    setStep(isNotified ? 5 : 4);
    return () => setStep(0);
  }, [isNotified]);

  useEffect(() => {
    // redefine default value "null" to indicate the recent booking and navigate to confiramtion page if rescheduling is cancelled
    !isNotified && setIsNotified(false);
  }, []);

  if (!job) {
    return (
      <Content.$ className={styles.confirmation} dataTestId="confirmation-page">
        <Skeleton />
      </Content.$>
    );
  }

  const customer = job.customer;
  const portalElement = document.getElementById('booking-progress-portal');

  return (
    <Content.$ className={styles.confirmation} dataTestId="confirmation-page">
      <Helmet>
        <title>Confirm booking & send notifications</title>
      </Helmet>
      {portalElement &&
        (ReactDOM.createPortal(
          <>
            {customer && <LegacyCustomerAlert.$ customer={customer} />}
            {job && (
              <JobConfirmation.$
                job={job}
                onEdit={() => {
                  setStep(0);
                  navigate(`/job/reschedule/${job?.ID}/select-reason`);
                }}
              />
            )}
          </>,
          portalElement,
        ) as ReactNode)}
      <Space.$ size={Space.Size.Large} direction={Space.Direction.Vertical}>
        <Intro.$ />
        <SendNotification.$
          isStandalone={true}
          isNotified={!!isNotified}
          onSubmit={handleSendNotifications}
          isLoading={isNotifying}
          initialValues={initialValues}
          emailOptions={emailOptions}
          smsOptions={smsOptions}
          hasScript
          hasSkipButton
          title="Appointment Confirmation"
        />
        {isNotified && (
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
            <FinalAssistance.$ />
            <Space.$ justify={Space.Justify.Center}>
              <Button.$
                type={Button.Type.Primary}
                dataTestId="complete-btn"
                onClick={() => {
                  navigate('/dashboard');
                  resetBookingProgress();
                  setStep(0);
                }}
                disabled={isNotifying}
              >
                You are all done! Return to the dashboard
              </Button.$>
            </Space.$>
            <Space.$ justify={Space.Justify.Center}>
              <CoreBmsJobLink jobId={job?.ID.toString()} />
            </Space.$>
          </Space.$>
        )}
      </Space.$>
    </Content.$>
  );
});
